import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Clock, LogOut } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { authApi } from '@/services/auth'
import { billingApi } from '@/services/billing'
import { getErrorMessage } from '@/lib/api'

export default function TrialExpiredPage() {
  const navigate = useNavigate()
  const [isRedirecting, setIsRedirecting] = useState(false)

  const billingQuery = useQuery({
    queryKey: ['billing-status'],
    queryFn: billingApi.getStatus,
    staleTime: 0,
    retry: false,
  })
  const status = billingQuery.data

  // Nothing to block any more (paid, or back within the free user limit).
  useEffect(() => {
    if (status && !status.is_locked) {
      navigate('/dashboard', { replace: true })
    }
  }, [status, navigate])

  const userLimit = status?.user_limit ?? 10
  const title =
    status?.lock_reason === 'subscription_ended' ? 'Your subscription has ended' : 'Your free trial has ended'
  const description =
    status?.lock_reason === 'subscription_ended'
      ? 'Your subscription was cancelled or its payment could not be collected. Subscribe again to keep using your account — your data and setup are safe.'
      : 'Upgrade now to keep using your account with no interruption to your data or setup.'

  const onUpgrade = async () => {
    setIsRedirecting(true)
    try {
      const { checkout_url } = await billingApi.createCheckoutSession()
      window.location.href = checkout_url
    } catch (error) {
      toast.error(getErrorMessage(error))
      setIsRedirecting(false)
    }
  }

  const onSignOut = async () => {
    await authApi.logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary/5 px-4 py-10">
      <Card className="w-full max-w-md border-primary/20 shadow-xl">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Clock className="h-7 w-7" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
          {status?.user_count !== undefined ? (
            <p className="pt-2 text-sm text-muted-foreground">
              Your organization has {status.user_count} users. Teams of up to {userLimit} users use FacilityPro
              free; customers and suppliers don&apos;t count.
            </p>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3">
          <Button type="button" className="w-full" disabled={isRedirecting} onClick={onUpgrade}>
            {isRedirecting ? <Spinner className="mr-2" /> : null}
            {status?.lock_reason === 'subscription_ended' ? 'Subscribe again' : 'Upgrade now'}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={onSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
